import { HTTPValidationError } from '@platform/client/schemas';
import { useState } from 'react';
import { FileUploadWithButton } from './FileUploadWithButton';
import { Modal, Spinner } from '@platform/components';
import { GreenCircleCheckmark } from '../../svg/GreenCircleCheckmark';
import { UseMutationResult } from '@tanstack/react-query';

type BatchImportMutation = Pick<
  UseMutationResult<unknown, unknown, File>,
  'mutate' | 'isPending' | 'isError' | 'isSuccess' | 'error' | 'reset'
>;

export const BatchImportModal = ({
  isOpen,
  onClose,
  recordType,
  templateFileUrl,
  mutation,
}: {
  isOpen: boolean;
  onClose: () => void;
  recordType: string;
  templateFileUrl: string;
  mutation: BatchImportMutation;
}) => {
  const [file, setFile] = useState<File>();
  const handleClose = () => {
    setFile(undefined);
    mutation.reset();
    onClose();
  };
  const isDone = mutation.isError || mutation.isSuccess;
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      variant='dialog'
      headerDivision='divider'
      title={`Import ${recordType}`}
      ignoreExtraPadding
      primaryCTA={{
        text: isDone ? 'Close' : `Import ${recordType}`,
        callback: isDone ? handleClose : () => file && mutation.mutate(file),
        isDisabled: () => !file || mutation.isPending,
      }}
      secondaryCTA={{
        text: 'Cancel',
        callback: handleClose,
      }}
    >
      <div className='flex flex-col justify-center items-center gap-4'>
        {!mutation.isPending && !mutation.isError && !mutation.isSuccess && (
          <>
            <p>
              Upload the completed {recordType} template below. You can download a copy{' '}
              <a className='blue-link' href={templateFileUrl} download>
                here
              </a>
              .
            </p>
            <div className='max-w-48'>
              <FileUploadWithButton
                onFileUpload={(file) => setFile(file)}
                acceptedFileTypes={['.csv']}
              />
            </div>
            {file && <p className='text-xs text-gray-400'>File name: {file.name}</p>}
          </>
        )}
        {mutation.isPending && (
          <>
            <Spinner />
            <p>
              Importing {recordType} from {file?.name}. This may take a few moments.
            </p>
          </>
        )}
        {mutation.isSuccess && (
          <>
            <GreenCircleCheckmark size={24} />
            <p>{recordType} submitted successfully</p>
          </>
        )}
        {mutation.isError && (
          <>
            <p>There was an error importing the file:</p>
            {(mutation.error as HTTPValidationError)?.detail?.map((err, i) => (
              <p key={i}>{err.msg}</p>
            ))}
            <p>
              Review the import file for missing values. If the issue continues contact tech team!
            </p>
          </>
        )}
      </div>
    </Modal>
  );
};
